/**
 * PORTED — RO-11-02 / #44587.
 * Origin: `excellent/app/_chrome/user-menu.tsx`.
 *
 * Changes: useDock from local stub (dock stack not yet ported). No next/*.
 */

import {
  Bell,
  BookUser,
  Bug,
  Database,
  HelpCircle,
  Menu,
  MessageSquare,
  Settings2,
  Star,
  Terminal,
} from "@excellent/ui/icons";
import {
  Button,
  cn,
  COUNT_BUBBLE,
  DropdownMenu,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  BugReportDialog,
  type ContextMenuItem,
} from "@excellent/ui";
import { useState } from "react";
import { useDock } from "./dock-context";

/**
 * The user affordance on the right of the nav: flat toolbar hamburger with
 * optional unread badge, opening account + tools menu.
 */
export function UserMenu({
  reviewCount,
  copilotEnabled,
}: {
  reviewCount: number;
  copilotEnabled: boolean;
}) {
  const { toggle: toggleTerminal } = useDock();
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const unread = reviewCount;

  const icon = (Glyph: typeof Bell) => (
    <Glyph strokeWidth={1.75} className="h-3.5 w-3.5" />
  );

  const items: ContextMenuItem[] = [
    ...(copilotEnabled
      ? [
          {
            label: "Chat",
            onSelect: () =>
              window.dispatchEvent(new CustomEvent("excellent:open-chat")),
            icon: icon(MessageSquare),
          } as ContextMenuItem,
          { type: "separator" } as ContextMenuItem,
        ]
      : []),
    { label: "Notifications", href: "/notifications", icon: icon(Bell) },
    { label: "Favorites", href: "/favorites", icon: icon(Star) },
    { type: "separator" },
    { label: "Database", href: "/database", icon: icon(Database) },
    { label: "Terminal", onSelect: toggleTerminal, icon: icon(Terminal) },
    { label: "Directory", href: "/directory", icon: icon(BookUser) },
    { type: "separator" },
    { label: "Help center", href: "/help", icon: icon(HelpCircle) },
    { label: "Report a bug", onSelect: () => setBugReportOpen(true), icon: icon(Bug) },
    { label: "Settings", href: "/settings", icon: icon(Settings2) },
  ];

  return (
    <>
      <Tooltip>
        <DropdownMenu items={items} align="end" widthPx={220}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="flat"
              size="icon-tb"
              aria-label={
                unread > 0 ? `User menu, ${unread} notifications` : "User menu"
              }
              className="relative"
            >
              <Menu strokeWidth={1.75} aria-hidden />
              {unread > 0 ? (
                <span
                  aria-hidden
                  className={cn(
                    COUNT_BUBBLE,
                    "absolute -right-0.5 -top-0.5 h-[15px] min-w-[15px] px-1 type-subtext leading-none ring-2 ring-background",
                  )}
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              ) : null}
            </Button>
          </TooltipTrigger>
        </DropdownMenu>
        <TooltipContent side="bottom">
          {unread > 0 ? `Account · ${unread > 99 ? "99+" : unread} need you` : "Account & tools"}
        </TooltipContent>
      </Tooltip>
      <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
    </>
  );
}
